import { ParseTableLabelDate } from 'app/utils/Parsers';
import Message from 'app/utils/Message';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdDelete, MdOutlineEdit } from 'react-icons/md';

const BLANK = '—';

// Derived-status code (server, utils/packageStatus.ts) -> pill class + label.
// There is no Paused state (D-05): the server still computes pauseReasons, this
// screen deliberately does not render them.
export const PACKAGE_STATUS_PILL = {
  ACTIVE: { className: 'bdg active', label: 'Active' },
  SCHEDULED: { className: 'bdg voilet', label: 'Scheduled' },
  SOLD_OUT: { className: 'bdg inactive', label: 'Sold out' },
  EXPIRED: { className: 'bdg neutral', label: 'Expired' },
  DEACTIVATED: { className: 'bdg neutral', label: 'Deactivated' },
};

// Dropdown options for the status filter (D-06). The all option has an empty
// value, which the hook turns into "no status key at all".
export const PACKAGE_STATUS_OPTIONS = [
  { label: 'All', value: '' },
  ...Object.entries(PACKAGE_STATUS_PILL).map(([value, { label }]) => ({
    label,
    value,
  })),
];

const numeric = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const NumberCell = ({ value }) => (
  <span style={{ display: 'block', textAlign: 'right' }}>
    {value === undefined || value === null || value === ''
      ? BLANK
      : numeric(value).toLocaleString('en-IN')}
  </span>
);

const DateCell = ({ value }) => ParseTableLabelDate(value) || BLANK;

// Gold pills, one per voucher, in member order (same gold as the buttons).
const GoldPillListCell = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return BLANK;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minWidth: 240 }}>
      {items.map((item, index) => (
        <span key={`${item}-${index}`} className='bdg gold'>
          {item || BLANK}
        </span>
      ))}
    </div>
  );
};

// Plain comma-separated text, like the Collection Panel's Store Name column.
// The store at index i belongs to the voucher at index i, so both columns list
// their entries in the same order.
const TextListCell = ({ items, minWidth }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return BLANK;
  }
  // A minimum width keeps long lists reading as lines, not one word per row.
  return (
    <span style={{ display: 'block', minWidth }}>
      {items.map((item) => item || BLANK).join(', ')}
    </span>
  );
};

const StatusCell = ({ code }) => {
  const pill = PACKAGE_STATUS_PILL[code];
  if (!pill) {
    return BLANK;
  }
  return <span className={pill.className}>{pill.label}</span>;
};

const ActionCell = ({ row, onEdit, onDelete }) => {
  const bought = !!row?.everBought;
  return (
    <div style={{ width: 'max-content' }}>
      <span
        role='button'
        tabIndex={0}
        aria-label={`Edit ${row?.title || 'bundle'}`}
        className='link cursor-pointer'
        onClick={() => onEdit && onEdit(row)}
      >
        <MdOutlineEdit />
      </span>
      {bought ? (
        // Cosmetic only (D-07): the server's delete refusal is the real
        // protection. Not the global `.disabled` class — it sets
        // pointer-events: none, which would also swallow the hover note.
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip id={`package-delete-note-${row?.id}`}>
              {Message.Package.ALREADY_BOUGHT_DELETE}
            </Tooltip>
          }
        >
          <span
            role='button'
            aria-disabled='true'
            aria-label={`Delete ${row?.title || 'bundle'} (unavailable)`}
            className='link'
            style={{ opacity: 0.4, cursor: 'not-allowed' }}
          >
            <MdDelete />
          </span>
        </OverlayTrigger>
      ) : (
        <span
          role='button'
          tabIndex={0}
          aria-label={`Delete ${row?.title || 'bundle'}`}
          className='link cursor-pointer'
          onClick={() => onDelete && onDelete(row)}
        >
          <MdDelete />
        </span>
      )}
    </div>
  );
};

// The Packages list columns, in the mockup's order. A factory, not a constant,
// because the Action cell closes over the hook's password gate:
//   buildPackageColumns({
//     onEdit: (row) => handlePasswordModel(row, 'edit'),
//     onDelete: (row) => handlePasswordModel(row, 'delete'),
//   })
//
// CALLER MUST MEMOISE the result (`useMemo`): PaginationTable memoises on the
// columns array's identity, so a fresh array each render refetches forever.
//
// Every column has an explicit `id` that PaginationTable's cell switch does not
// intercept. That switch matches on id ('Actions', 'Status', 'Edit Action',
// ...) and would otherwise print a boolean Active/Inactive and unconditional
// edit/delete icons in place of these cells.
export const buildPackageColumns = ({ onEdit, onDelete } = {}) => [
  {
    Header: 'Bundle Title',
    id: 'packageTitle',
    accessor: 'title',
    Cell: ({ row }) => row.original?.title || BLANK,
  },
  {
    Header: 'Mall',
    id: 'packageMall',
    accessor: 'mallName',
    Cell: ({ row }) => row.original?.mallName || BLANK,
  },
  {
    Header: 'Vouchers',
    id: 'packageVouchers',
    accessor: 'voucherTitles',
    disableSortBy: true,
    Cell: ({ row }) => <GoldPillListCell items={row.original?.voucherTitles} />,
  },
  {
    // Each voucher's store, in the same order as the Vouchers column.
    Header: 'Store Name',
    id: 'packageStores',
    accessor: 'voucherStoreNames',
    disableSortBy: true,
    Cell: ({ row }) => <TextListCell items={row.original?.voucherStoreNames} minWidth={150} />,
  },
  {
    // The server's summed figure; never re-summed here (REQ-05).
    Header: 'Bundle Points',
    id: 'packagePoints',
    accessor: 'loyaltyPoints',
    Cell: ({ row }) => <NumberCell value={row.original?.loyaltyPoints} />,
  },
  {
    Header: 'Total Bundles',
    id: 'packageTotal',
    accessor: 'packageCount',
    Cell: ({ row }) => <NumberCell value={row.original?.packageCount} />,
  },
  {
    Header: 'Remaining',
    id: 'packageRemaining',
    accessor: (row) =>
      Math.max(0, numeric(row?.packageCount) - numeric(row?.purchasedCount)),
    Cell: ({ value }) => <NumberCell value={value} />,
  },
  {
    Header: 'Sold',
    id: 'packageSold',
    accessor: 'purchasedCount',
    Cell: ({ row }) => <NumberCell value={row.original?.purchasedCount ?? 0} />,
  },
  {
    // Calendar labels (YYYY-MM-DD): ParseTableLabelDate only, never shifted.
    Header: 'Start Date',
    id: 'packageStartDate',
    accessor: 'startDate',
    Cell: ({ value }) => <DateCell value={value} />,
  },
  {
    Header: 'Expiry Date',
    id: 'packageExpiryDate',
    accessor: 'expiryDate',
    Cell: ({ value }) => <DateCell value={value} />,
  },
  {
    // Server-derived status only; the row's boolean `status` is not read.
    Header: 'Status',
    id: 'packageStatus',
    accessor: 'derivedStatus',
    Cell: ({ value }) => <StatusCell code={value} />,
  },
  {
    Header: 'Action',
    id: 'packageActions',
    accessor: (row) => row?.id,
    disableSortBy: true,
    Cell: ({ row }) => (
      <ActionCell row={row.original} onEdit={onEdit} onDelete={onDelete} />
    ),
  },
];

export default buildPackageColumns;
